import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Card, PageHeader, Table, Td, Th } from "@/components/ui";
import { popup } from "@/components/popup";
import { TextEditModal } from "@/components/TextEditModal";

interface StepText {
  key: string;
  ru: string;
}
interface Btn {
  key: string;
  label: string;
}
interface Step {
  enabled: boolean;
  offer_enabled?: boolean;
  percent: number;
  buttons: Btn[];
  stage: string;
  when: string;
  title: string;
  badge: string;
  sent: number;
  vpn: number;
  proxy: number;
  width: number;
  bought: number;
  texts: StepText[];
}
interface FunnelData {
  enabled: boolean;
  days: number;
  kind: string;
  totals: {
    sent: number;
    users: number;
    bought: number;
    conversion: number;
    promo_issued: number;
    promo_used: number;
    promo_uses: number;
  };
  steps: Step[];
  survey: {
    percent: number;
    asked: number;
    answered: number;
    answers: { kind: string; option: string; count: number }[];
  };
  other: {
    stage: string; title: string; when: string; enabled: boolean;
    texts: StepText[]; buttons: Btn[];
  }[];
}

const OPTION_LABELS: Record<string, string> = {
  price: "Дорого",
  speed: "Медленная скорость",
  devices: "Мало устройств",
  unstable: "Часто отваливается",
  no_need: "Больше не нужно",
  other: "Другая причина",
};

function renderTg(src: string): string {
  let s = src.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  for (const tag of ["b", "i", "u", "s", "code"]) {
    s = s
      .replace(new RegExp(`&lt;${tag}&gt;`, "g"), `<${tag}>`)
      .replace(new RegExp(`&lt;/${tag}&gt;`, "g"), `</${tag}>`);
  }
  return s.replace(/\n/g, "<br/>");
}

const n = (v: number) => v.toLocaleString("ru-RU");

/** Фильтр-чип. */
function Chip({
  active,
  onClick,
  tone,
  children,
}: {
  active: boolean;
  onClick: () => void;
  tone?: "vpn" | "proxy";
  children: React.ReactNode;
}) {
  const toneCls = !active
    ? "text-zinc-400 border-white/[0.07] hover:text-zinc-200"
    : tone === "vpn"
      ? "text-sky-300 border-sky-400/40 bg-sky-400/10"
      : tone === "proxy"
        ? "text-violet-300 border-violet-400/40 bg-violet-400/10"
        : "text-zinc-100 border-white/[0.14] bg-white/[0.05]";
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${toneCls}`}
    >
      {children}
    </button>
  );
}

interface HistoryRow {
  id: number;
  stage: string;
  kind: string | null;
  sent_at: string;
  user: { id: number; telegram_id: number; username: string | null; first_name: string | null };
  promo: { code: string; percent: number; used: boolean; product: string } | null;
}

const STAGE_LABELS: Record<string, string> = {
  trial_offer_60m: "Скидка на час",
  h12: "Через 12 часов",
  h24: "Через сутки",
  before_6h: "За 6 часов",
  before_2h: "За 2 часа",
  before_1h: "За час",
  expired: "Проба завершена",
  survey: "Опрос",
  ladder_10: "Возврат −10%",
  ladder_20: "Возврат −20%",
  ladder_30: "Возврат −30%",
  ladder_40: "Возврат −40%",
};

/** Кому что ушло: журнал отправок с фильтрами и переходом в карточку юзера. */
function History() {
  const [stage, setStage] = useState("");
  const [kind, setKind] = useState<"all" | "vpn" | "proxy">("all");
  const [used, setUsed] = useState<"all" | "yes" | "no">("all");
  const [page, setPage] = useState(0);
  const perPage = 50;

  const q = useQuery<{ items: HistoryRow[]; total: number; stages: string[] }>({
    queryKey: ["funnel-history", stage, kind, used, page],
    queryFn: () =>
      api(
        `/api/admin/funnel/history?limit=${perPage}&offset=${page * perPage}` +
          `&stage=${stage}&kind=${kind}&used=${used}`,
      ),
  });

  const d = q.data;
  const pages = d ? Math.ceil(d.total / perPage) : 0;

  return (
    <Card>
      <div className="flex items-baseline gap-3 mb-3 flex-wrap">
        <div className="text-sm font-semibold text-zinc-200">История</div>
        {d && <div className="text-xs text-zinc-500">{n(d.total)} записей</div>}
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <select
          value={stage}
          onChange={(e) => {
            setStage(e.target.value);
            setPage(0);
          }}
          className="h-7 px-2 rounded-lg bg-white/[0.04] border border-white/[0.07] text-xs text-zinc-200"
        >
          <option value="">Все сообщения</option>
          {(d?.stages ?? []).map((st) => (
            <option key={st} value={st}>
              {STAGE_LABELS[st] ?? st}
            </option>
          ))}
        </select>
        {(["all", "vpn", "proxy"] as const).map((k) => (
          <Chip key={k} active={kind === k} onClick={() => { setKind(k); setPage(0); }}
                tone={k === "all" ? undefined : k}>
            {k === "all" ? "Все" : k === "vpn" ? "VPN" : "Прокси"}
          </Chip>
        ))}
        <div className="w-px h-5 bg-white/[0.08] mx-1" />
        {([["all", "Все"], ["no", "Промокод не применён"], ["yes", "Применён"]] as const).map(
          ([v, label]) => (
            <Chip key={v} active={used === v} onClick={() => { setUsed(v); setPage(0); }}>
              {label}
            </Chip>
          ),
        )}
      </div>

      {q.isLoading ? (
        <div className="text-sm text-zinc-500">Загрузка…</div>
      ) : !d?.items.length ? (
        <div className="text-sm text-zinc-500">Ничего не найдено.</div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <thead>
              <tr>
                <Th>Когда</Th>
                <Th>Юзер</Th>
                <Th>Сообщение</Th>
                <Th>Продукт</Th>
                <Th>Промокод</Th>
              </tr>
            </thead>
            <tbody>
              {d.items.map((r) => (
                <tr key={r.id}>
                  <Td className="text-zinc-500 text-xs whitespace-nowrap">
                    {new Date(r.sent_at).toLocaleString("ru", {
                      day: "2-digit", month: "2-digit",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </Td>
                  <Td>
                    <a
                      href={`/users/${r.user.id}`}
                      className="text-brand hover:underline whitespace-nowrap"
                    >
                      {r.user.username
                        ? `@${r.user.username}`
                        : r.user.first_name || r.user.telegram_id}
                    </a>
                  </Td>
                  <Td className="whitespace-nowrap">{STAGE_LABELS[r.stage] ?? r.stage}</Td>
                  <Td>
                    {r.kind && (
                      <span
                        className={`text-[11px] px-1.5 py-0.5 rounded ${
                          r.kind === "vpn"
                            ? "text-sky-300 bg-sky-400/10"
                            : "text-violet-300 bg-violet-400/10"
                        }`}
                      >
                        {r.kind}
                      </span>
                    )}
                  </Td>
                  <Td>
                    {r.promo ? (
                      <span className="flex items-center gap-2 whitespace-nowrap">
                        <code className="text-zinc-300">{r.promo.code}</code>
                        <span className="text-emerald-400 text-xs">−{r.promo.percent}%</span>
                        {r.promo.used ? (
                          <span className="text-[11px] text-emerald-400">применён</span>
                        ) : (
                          <span className="text-[11px] text-zinc-600">не применён</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center gap-2 mt-3">
          <Button variant="ghost" disabled={page === 0} onClick={() => setPage(page - 1)}>
            ← назад
          </Button>
          <span className="text-xs text-zinc-500">
            {page + 1} из {pages}
          </span>
          <Button variant="ghost" disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>
            вперёд →
          </Button>
        </div>
      )}
    </Card>
  );
}

/** Размер скидки — правится прямо в строке, без захода в настройки. */
function PercentBadge({
  value,
  isSurvey,
  onChange,
}: {
  value: number;
  isSurvey: boolean;
  onChange: (v: number) => void;
}) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(String(value));

  if (!edit) {
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          setDraft(String(value));
          setEdit(true);
        }}
        onKeyDown={(e) => e.key === "Enter" && setEdit(true)}
        title="нажмите, чтобы изменить размер скидки"
        className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded text-emerald-400 bg-emerald-400/10 shrink-0 cursor-pointer hover:bg-emerald-400/20"
      >
        {isSurvey ? `${value}% за ответ` : `−${value}%`}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
      <input
        autoFocus
        type="number"
        min={0}
        max={90}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            onChange(Math.max(0, Math.min(90, Number(draft) || 0)));
            setEdit(false);
          }
          if (e.key === "Escape") setEdit(false);
        }}
        onBlur={() => {
          onChange(Math.max(0, Math.min(90, Number(draft) || 0)));
          setEdit(false);
        }}
        className="w-14 h-6 px-1.5 rounded bg-black/40 border border-emerald-400/40 text-xs text-emerald-300 text-center"
      />
      <span className="text-[10px] text-zinc-500">%</span>
    </span>
  );
}

export default function Funnel() {
  const qc = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  const [edit, setEdit] = useState<{ key: string; title: string } | null>(null);
  const [kind, setKind] = useState<"all" | "vpn" | "proxy">("all");
  const [days, setDays] = useState(30);

  const q = useQuery<FunnelData>({
    queryKey: ["funnel", kind, days],
    queryFn: () => api(`/api/admin/funnel?kind=${kind}&days=${days}`),
  });

  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      api(`/api/admin/funnel/toggle?enabled=${enabled}`, { method: "POST" }),
    onSuccess: (_r, enabled) => {
      qc.invalidateQueries({ queryKey: ["funnel"] });
      popup({
        tone: enabled ? "success" : "info",
        title: enabled ? "Рассылка включена" : "Рассылка выключена",
        body: enabled
          ? "Бот начнёт отправлять сообщения по расписанию."
          : "Автосообщения больше не уходят.",
      });
    },
    onError: (e) => popup({ tone: "error", title: "Ошибка", body: (e as Error).message }),
  });

  const setPercent = useMutation({
    mutationFn: ({ stage, percent }: { stage: string; percent: number }) =>
      api(`/api/admin/funnel/step/${stage}/percent?percent=${percent}`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["funnel"] }),
    onError: (e) => popup({ tone: "error", title: "Ошибка", body: (e as Error).message }),
  });

  const toggleStep = useMutation({
    mutationFn: ({ stage, enabled }: { stage: string; enabled: boolean }) =>
      api(`/api/admin/funnel/step/${stage}/toggle?enabled=${enabled}`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["funnel"] }),
    onError: (e) => popup({ tone: "error", title: "Ошибка", body: (e as Error).message }),
  });

  const toggleOffer = useMutation({
    mutationFn: ({ stage, enabled }: { stage: string; enabled: boolean }) =>
      api(`/api/admin/funnel/step/${stage}/offer?enabled=${enabled}`, {
        method: "POST",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["funnel"] }),
    onError: (e) => popup({ tone: "error", title: "Ошибка", body: (e as Error).message }),
  });

  if (q.isLoading) return <div className="p-8 text-zinc-400">Загрузка…</div>;
  const d = q.data;
  if (!d) return null;
  const t = d.totals;

  return (
    <div className="p-4 md:p-8 space-y-4">
      <PageHeader
        title="Автосообщения"
        right={
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${
                d.enabled
                  ? "text-emerald-400 border-emerald-400/35 bg-emerald-400/10"
                  : "text-amber-400 border-amber-400/35 bg-amber-400/10"
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current" />
              {d.enabled ? "Рассылка идёт" : "Выключено"}
            </span>
            <Button
              variant={d.enabled ? "danger" : "primary"}
              onClick={() => toggle.mutate(!d.enabled)}
              disabled={toggle.isPending}
            >
              {d.enabled ? "Выключить" : "Включить"}
            </Button>
          </div>
        }
      />

      {/* ── Фильтры ── */}
      <div className="flex items-center gap-2 flex-wrap">
        <Chip active={kind === "all"} onClick={() => setKind("all")}>
          Все
        </Chip>
        <Chip active={kind === "vpn"} onClick={() => setKind("vpn")} tone="vpn">
          VPN
        </Chip>
        <Chip active={kind === "proxy"} onClick={() => setKind("proxy")} tone="proxy">
          Прокси
        </Chip>
        <div className="w-px h-5 bg-white/[0.08] mx-1" />
        {[7, 30, 90].map((v) => (
          <Chip key={v} active={days === v} onClick={() => setDays(v)}>
            {v} дней
          </Chip>
        ))}
      </div>

      {/* ── Итоги ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">Отправлено</div>
          <div className="text-2xl font-bold tabular-nums mt-0.5">{n(t.sent)}</div>
          <div className="text-xs text-zinc-500 mt-0.5">{n(t.users)} чел.</div>
        </Card>
        <Card>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">Промокодов выдано</div>
          <div className="text-2xl font-bold tabular-nums mt-0.5">{n(t.promo_issued)}</div>
          <div className="text-xs text-zinc-500 mt-0.5">персональные</div>
        </Card>
        <Card>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">Использовано</div>
          <div
            className={`text-2xl font-bold tabular-nums mt-0.5 ${
              t.promo_used === 0 && t.promo_issued > 0 ? "text-rose-400" : ""
            }`}
          >
            {n(t.promo_used)}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {t.promo_issued > 0
              ? `${((t.promo_used / t.promo_issued) * 100).toFixed(1)}% от выданных`
              : "—"}
          </div>
        </Card>
        <Card>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">Купили после</div>
          <div
            className={`text-2xl font-bold tabular-nums mt-0.5 ${
              t.conversion < 1 ? "text-rose-400" : "text-emerald-400"
            }`}
          >
            {n(t.bought)}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">{t.conversion}% от получателей</div>
        </Card>
      </div>

      {!d.enabled && (
        <div className="rounded-xl border border-amber-400/25 bg-amber-400/[0.06] px-4 py-3 text-sm text-amber-300">
          Рассылка выключена — сообщения не уходят. Проверьте тексты, затем включите.
        </div>
      )}

      {/* ── Водопад ── */}
      <Card>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-zinc-200">Цепочка сообщений</div>
          <div className="flex items-center gap-4 text-[11px] text-zinc-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="w-2 h-2 rounded-sm bg-sky-400" />VPN
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="w-2 h-2 rounded-sm bg-violet-400" />Прокси
            </span>
          </div>
        </div>

        <div className="-mx-2">
          {d.steps.map((s) => {
            const isOpen = open === s.stage;
            const vpnPct = s.sent ? (s.vpn / s.sent) * 100 : 0;
            const proxyPct = s.sent ? (s.proxy / s.sent) * 100 : 0;
            return (
              <div key={s.stage}>
                <div
                  className={`w-full grid grid-cols-[38px_92px_1fr_150px_74px] items-center gap-3 px-2 py-2 rounded-lg transition-colors ${
                    isOpen ? "bg-white/[0.04]" : "hover:bg-white/[0.02]"
                  } ${s.enabled ? "" : "opacity-45"}`}
                >
                  {/* Тумблер: сообщение можно выключить отдельно, не гася всю цепочку */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleStep.mutate({ stage: s.stage, enabled: !s.enabled });
                    }}
                    title={s.enabled ? "Отключить это сообщение" : "Включить это сообщение"}
                    className={`w-8 h-[18px] rounded-full relative transition-colors shrink-0 ${
                      s.enabled ? "bg-brand/70" : "bg-white/[0.12]"
                    }`}
                  >
                    <span
                      className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white transition-all ${
                        s.enabled ? "left-[16px]" : "left-[2px]"
                      }`}
                    />
                  </button>
                  <button
                    onClick={() => setOpen(isOpen ? null : s.stage)}
                    className="contents text-left"
                  >
                  <span className="text-xs text-zinc-500 tabular-nums">{s.when}</span>
                  <span className="text-sm text-zinc-100 flex items-center gap-2 min-w-0">
                    <span className="truncate">{s.title}</span>
                    <PercentBadge
                      value={s.percent}
                      isSurvey={s.stage === "survey"}
                      onChange={(v) => setPercent.mutate({ stage: s.stage, percent: v })}
                    />
                    {s.offer_enabled !== undefined && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOffer.mutate({ stage: s.stage, enabled: !s.offer_enabled });
                        }}
                        title={s.offer_enabled
                          ? "Со скидкой (промокод в сообщении). Нажми — сделать инфо-сообщением без скидки"
                          : "Без скидки — инфо-сообщение. Нажми — включить скидку"}
                        className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${
                          s.offer_enabled
                            ? "border-emerald-400/30 text-emerald-400/90 bg-emerald-400/10"
                            : "border-white/[0.1] text-zinc-500"
                        }`}
                      >
                        {s.offer_enabled ? "со скидкой" : "без скидки"}
                      </button>
                    )}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="flex-1 h-[18px] rounded bg-white/[0.05] overflow-hidden flex">
                      {/* ширина всей полосы — доля от самого массового шага,
                          внутри делится по продукту */}
                      <span className="flex h-full" style={{ width: `${s.width}%` }}>
                        <i className="bg-sky-400/80 h-full" style={{ width: `${vpnPct}%` }} />
                        <i className="bg-violet-400/80 h-full" style={{ width: `${proxyPct}%` }} />
                      </span>
                    </span>
                    <span className="text-xs text-zinc-400 tabular-nums w-11 text-right">
                      {n(s.sent)}
                    </span>
                  </span>
                  <span
                    className={`text-xs tabular-nums text-right ${
                      s.bought > 0 ? "text-emerald-400" : "text-zinc-600"
                    }`}
                  >
                    {s.bought > 0 ? `${s.bought} куп.` : "—"}
                  </span>
                  </button>
                </div>

                {isOpen && (
                  <div className="pl-[104px] pr-2 pb-4 pt-1 space-y-3">
                    {s.texts.map((tx) => (
                      <div key={tx.key}>
                        <div
                          className="rounded-2xl rounded-tl-sm bg-[#17222e] border border-white/[0.05] px-3.5 py-2.5 text-sm text-zinc-100 leading-relaxed max-w-lg"
                          dangerouslySetInnerHTML={{ __html: renderTg(tx.ru) }}
                        />
                        <div className="flex items-center gap-3 mt-2">
                          <code className="text-[11px] text-zinc-600">{tx.key}</code>
                          <button
                            onClick={() => setEdit({ key: tx.key, title: `Текст · ${s.title}` })}
                            className="text-xs text-brand hover:underline"
                          >
                            править текст
                          </button>
                        </div>
                      </div>
                    ))}
                    {s.buttons?.length > 0 && (
                      <div className="max-w-lg space-y-1.5">
                        {s.buttons.map((b) => (
                          <div key={b.key}>
                            <div className="rounded-lg bg-[#2b5278] text-zinc-50 text-sm px-3.5 py-2 text-center">
                              {b.label}
                            </div>
                            <div className="flex items-center gap-3 mt-1">
                              <code className="text-[11px] text-zinc-600">{b.key}</code>
                              <button onClick={() => setEdit({ key: b.key, title: `Кнопка · ${s.title}` })}
                                      className="text-xs text-brand hover:underline">
                                править кнопку
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* ── Остальные автосообщения (вне воронки) ── */}
      {d.other?.length > 0 && (
        <Card>
          <div className="flex items-baseline gap-3 mb-3">
            <div className="text-sm font-semibold text-zinc-200">Остальные автосообщения</div>
            <div className="text-xs text-zinc-500">
              работают независимо от цепочки
            </div>
          </div>
          <div className="-mx-2">
            {d.other.map((o) => {
              const isOpen = open === `other:${o.title}`;
              return (
                <div key={o.title}>
                  <div
                    className={`w-full grid grid-cols-[38px_112px_1fr] items-center gap-3 px-2 py-2 rounded-lg transition-colors ${
                      isOpen ? "bg-white/[0.04]" : "hover:bg-white/[0.02]"
                    } ${o.enabled ? "" : "opacity-45"}`}
                  >
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleStep.mutate({ stage: o.stage, enabled: !o.enabled });
                      }}
                      title={o.enabled ? "Отключить это сообщение" : "Включить это сообщение"}
                      className={`w-8 h-[18px] rounded-full relative transition-colors shrink-0 ${
                        o.enabled ? "bg-brand/70" : "bg-white/[0.12]"
                      }`}
                    >
                      <span
                        className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white transition-all ${
                          o.enabled ? "left-[16px]" : "left-[2px]"
                        }`}
                      />
                    </button>
                    <button
                      onClick={() => setOpen(isOpen ? null : `other:${o.title}`)}
                      className="contents text-left"
                    >
                      <span className="text-xs text-zinc-500 leading-tight">{o.when}</span>
                      <span className="text-sm text-zinc-100 truncate">{o.title}</span>
                    </button>
                  </div>
                  {isOpen && (
                    <div className="pl-[104px] pr-2 pb-4 pt-1 space-y-3">
                      {o.texts.map((tx) => (
                        <div key={tx.key}>
                          <div
                            className="rounded-2xl rounded-tl-sm bg-[#17222e] border border-white/[0.05] px-3.5 py-2.5 text-sm text-zinc-100 leading-relaxed max-w-lg"
                            dangerouslySetInnerHTML={{ __html: renderTg(tx.ru) }}
                          />
                          <div className="flex items-center gap-3 mt-2">
                            <code className="text-[11px] text-zinc-600">{tx.key}</code>
                            <button onClick={() => setEdit({ key: tx.key, title: `Текст · ${o.title}` })}
                                    className="text-xs text-brand hover:underline">
                              править текст
                            </button>
                          </div>
                        </div>
                      ))}
                      {o.buttons?.length > 0 && (
                        <div className="max-w-lg space-y-1.5">
                          {o.buttons.map((b) => (
                            <div key={b.key}>
                              <div className="rounded-lg bg-[#2b5278] text-zinc-50 text-sm px-3.5 py-2 text-center">
                                {b.label}
                              </div>
                              <div className="flex items-center gap-3 mt-1">
                                <code className="text-[11px] text-zinc-600">{b.key}</code>
                                <button onClick={() => setEdit({ key: b.key, title: `Кнопка · ${o.title}` })}
                                        className="text-xs text-brand hover:underline">
                                  править кнопку
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Опрос ── */}
      <Card>
        <div className="flex items-baseline gap-3 mb-3">
          <div className="text-sm font-semibold text-zinc-200">Почему не продлили</div>
          <div className="text-xs text-zinc-500">
            {n(d.survey.asked)} спросили · {n(d.survey.answered)} ответили
            {d.survey.asked > 0 &&
              ` · ${((d.survey.answered / d.survey.asked) * 100).toFixed(0)}%`}
          </div>
          <span className="ml-auto text-[11px] px-2 py-0.5 rounded text-sky-300 bg-sky-400/10">
            {d.survey.percent}% за ответ
          </span>
        </div>
        {d.survey.answered === 0 ? (
          <div className="text-sm text-zinc-500">Ответов пока нет.</div>
        ) : (
          <div className="space-y-1.5">
            {d.survey.answers.map((a) => {
              const pct = (a.count / d.survey.answered) * 100;
              return (
                <div
                  key={`${a.kind}-${a.option}`}
                  className="grid grid-cols-[46px_170px_1fr_70px] gap-3 items-center"
                >
                  <span className="text-[11px] uppercase text-zinc-500">{a.kind}</span>
                  <span className="text-sm text-zinc-200 truncate">
                    {OPTION_LABELS[a.option] ?? a.option}
                  </span>
                  <span className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
                    <i
                      className={`block h-full rounded-full ${
                        a.kind === "vpn" ? "bg-sky-400" : "bg-violet-400"
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="text-xs text-zinc-400 tabular-nums text-right">
                    {a.count} · {pct.toFixed(0)}%
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <History />

      <TextEditModal
        contentKey={edit?.key ?? null}
        title={edit?.title}
        onClose={() => setEdit(null)}
        onSaved={() => qc.invalidateQueries({ queryKey: ["funnel"] })}
      />
    </div>
  );
}
